/** @module components/searchInput */
import baustein from "baustein";
import analytics from "web/script/analytics/analytics";
import forms from "web/script/dom/forms";
import globals from "web/script/modules/globals";
import strings from "web/script/utils/strings";
import traversal from "web/script/dom/traversal";
import { gettext } from "web/script/modules/django-i18n";
import { getSearchPageURL } from "web/script/routing/routes";
import { watchState } from "web/redux/state-watcher";
import store from "web/redux/store";
import { closeAppHeaderSearch } from "web/redux/ducks/app-header-search";
import searchMixin from "./mixins/search-mixin";

// Selectors
const SEARCH_INPUT_SELECTOR = 'input[name="q"]';
const USER_META_GENDER_SELECTOR = 'input[name="meta_gender"]';
const AUTOCOMPLETE_WRAPPER_SELECTOR = ".app-header-search__autocomplete-wrapper";
const SEARCH_SUBMIT_SELECTOR = ".app-header-search__submit-button";
const CLEAR_TEXT_BUTTON_SELECTOR = ".app-header-search__clear-text-button";
const CANCEL_BUTTON_SELECTOR = ".app-header-search__cancel-button";
const APP_HEADER_BAR_BURGER = ".app-header-bar-burger";

// Analytics category
const EVENT_CATEGORY = "nav_search";

function showAppHeaderSearch(state) {
    return state.appHeaderSearchReducer.open;
}

/**
 * @borrows all methods from components/search-input
 * @todo either refactor into a mixin or delete this or search-input after AB test has run
 *
 * @see components/search-input
 */
export default baustein.register("app-header-search", {
    mixins: [searchMixin],
    tagName: "form",
    shouldCloseAutosuggest: true,

    init() {
        this.searchBar = document.querySelector(APP_HEADER_BAR_BURGER);
        if (this.searchBar) {
            this.unsubscribeToStateWatcher = watchState(
                showAppHeaderSearch,
                (appHeaderSearchOpen) => {
                    appHeaderSearchOpen ? this.show() : this.close();
                }
            );
        }
    },

    setupEvents: function(add) {
        add("blur", SEARCH_INPUT_SELECTOR, this.onBlur);
        add("touchstart", SEARCH_SUBMIT_SELECTOR, this.onSubmit);
        add("mousedown", SEARCH_SUBMIT_SELECTOR, this.onSubmit);
        add("click", CLEAR_TEXT_BUTTON_SELECTOR, this._clearSearchText);
        add("click", CANCEL_BUTTON_SELECTOR, this.onCancel);
    },

    show() {
        this.searchBar.classList.toggle("hidden", false);
        const searchInput = this.el.querySelector(SEARCH_INPUT_SELECTOR);
        searchInput.focus();
    },

    close() {
        this.searchBar.classList.toggle("hidden", true);
    },

    /**
     * When the search input is focused we will make sure that the auto-complete component knows
     * the current search term and then show it.
     */
    onFocus() {
        this.emit("searchFocused");

        this.closeJoinCallout();
        const autoComplete = this._getAutoComplete();
        const query = this.getSearchQuery();

        autoComplete.updateOptions({
            query: query,
            gender: this.getCurrentGender(),
        });

        const clearTextEl = this.el.querySelector(CLEAR_TEXT_BUTTON_SELECTOR);
        if (clearTextEl && query) {
            clearTextEl.classList.remove("hidden");
        }

        const cancelButton = this.el.querySelector(CANCEL_BUTTON_SELECTOR);
        if (cancelButton) {
            cancelButton.classList.toggle("hidden", false);
        }

        this._showAutoComplete();
        this.setSearchTerm(query);
        this.setGlobalHandler("click", this.onGlobalClick);
        analytics.event(EVENT_CATEGORY, "search_input_focused");
    },

    /**
     * Hide the autocomplete when the Search Input is blurred.
     */
    onBlur() {
        this.emit("searchBlurred");

        if (this.shouldCloseAutosuggest) {
            this._hideAutoComplete();
        } else {
            this.shouldCloseAutosuggest = true;
        }

        if (!this.searchBar) {
            const cancelButton = this.el.querySelector(CANCEL_BUTTON_SELECTOR);
            if (cancelButton) {
                cancelButton.classList.toggle("hidden", true);
            }
        }
    },

    /**
     * Close the search and show the first stage of the menu bar.
     */
    onCancel() {
        if (this.shouldCloseAutosuggest) {
            this._hideAutoComplete();
        } else {
            this.shouldCloseAutosuggest = true;
        }

        const clearTextEl = this.el.querySelector(CLEAR_TEXT_BUTTON_SELECTOR);
        if (clearTextEl) {
            clearTextEl.classList.add("hidden");
        }

        if (this.searchBar) {
            store.dispatch(closeAppHeaderSearch());
            analytics.event("header", "clicked", "burger_menu_search_button_close");
        } else {
            const cancelButton = this.el.querySelector(CANCEL_BUTTON_SELECTOR);
            if (cancelButton) {
                cancelButton.classList.toggle("hidden", true);
            }
        }
    },

    /**
     * Global click event handler. If the click was outside of the component this should close
     * the auto-complete, otherwise it should not do anything.
     * @param  {Event} event
     */
    onGlobalClick(event) {
        if (traversal.contains(this.el, event.target)) {
            return;
        }

        this.onBlur();
        this.releaseGlobalHandler("click", this.onGlobalClick);
    },

    /**
     * Handler for the user selecting a suggestion in the auto-complete component.
     * @param {object} event The suggestions object passed as the event from the auto-complete component.
     */
    onSuggestionSelect(event) {
        this.shouldCloseAutosuggest = false;
        var analyticsAction = this._getAnalyticsEventAction("click", event.suggestion);

        this.setSearchQuery(this._getQueryFromSuggestion(event));

        if (event.suggestion.type === "query") {
            return analytics.event(EVENT_CATEGORY, analyticsAction, event.index).then(() => {
                this.submitSearch();
            });
        } else if (["category", "profile"].includes(event.suggestion.type)) {
            return analytics.event(EVENT_CATEGORY, analyticsAction).then(function() {
                globals.window.location = event.suggestion.path;
            });
        }
    },

    /**
     * Handles the submit event of the form.
     * @param {Event} event
     */
    onSubmit(event) {
        this.closeJoinCallout();
        var activeSuggestion = this._getAutoComplete().getActiveSuggestion();
        var eventAction;

        event.preventDefault();
        this.shouldCloseAutosuggest = false;

        // Don't submit empty searches.
        if (this.getSearchQuery().length === 0) {
            return;
        }

        if (
            activeSuggestion &&
            ["category", "profile"].includes(activeSuggestion.suggestion.type)
        ) {
            eventAction = this._getAnalyticsEventAction("enter", activeSuggestion.suggestion);

            analytics.event(EVENT_CATEGORY, eventAction).then(function() {
                globals.window.location = activeSuggestion.suggestion.path;
            });

            return;
        } else if (activeSuggestion) {
            eventAction = this._getAnalyticsEventAction("enter", activeSuggestion.suggestion);
            analytics.event(EVENT_CATEGORY, eventAction, activeSuggestion.index.toString());
        } else {
            let label = "without_suggestions";

            if (this._getAutoComplete().isShowingSuggestions()) {
                label = "with_suggestions";
            }
            analytics.event(EVENT_CATEGORY, "submit_search_input", label);
        }
        this.submitSearch();
    },

    onInput() {
        // Hide / unhide clear text button depending on whether there's text on input field
        const clearTextEl = this.el.querySelector(CLEAR_TEXT_BUTTON_SELECTOR);
        if (!clearTextEl) {
            return;
        }
        const query = this.getSearchQuery();
        if (query) {
            clearTextEl.classList.remove("hidden");
        } else {
            clearTextEl.classList.add("hidden");
        }
    },

    /**
     * Submits the current search by pushing a new state into the browsers history.
     * Also saves the submitted search term for future use.
     */
    submitSearch() {
        var form = this.el;
        var params = forms.serialize(form);
        params.q = params.q.trim();
        params.term = params.term.trim();

        var autoComplete = this._getAutoComplete();
        var gender = this.getCurrentGender();
        autoComplete.updateOptions({
            query: params.q,
            gender: gender,
        });

        this._hideAutoComplete();
        this.blur();
        this.emit("submitSearch");

        let target = new URL(getSearchPageURL(), globals.window.location.href);
        target.searchParams.set("q", params.q);
        target.searchParams.set("term", params.term);
        target.searchParams.set("meta_gender", gender);
        globals.window.location = target.href;
    },

    /**
     * Get the user gender
     */
    getCurrentGender() {
        return this.el.querySelector(USER_META_GENDER_SELECTOR).value;
    },

    /**
     * Changes placeholder text and meta gender when menu opened / closed
     */
    onMenuGenderChange(gender) {
        const searchInput = this.el.querySelector(SEARCH_INPUT_SELECTOR);
        if (gender === "men") {
            searchInput.placeholder = gettext("header.search.placeholder_for_men");
        } else {
            searchInput.placeholder = gettext("header.search.placeholder_for_women");
        }
        const genderInput = this.el.querySelector(USER_META_GENDER_SELECTOR);
        genderInput.value = strings.capitalize(gender);
    },

    /**
     * Removes whatever text has been introduced in the Search input element.
     *
     * @returns {undefined}
     */
    _clearSearchText() {
        const searchEl = this.el.querySelector(SEARCH_INPUT_SELECTOR);
        searchEl.focus();
        searchEl.value = "";
        const clearTextEl = this.el.querySelector(CLEAR_TEXT_BUTTON_SELECTOR);
        if (clearTextEl) {
            clearTextEl.classList.add("hidden");
        }
    },

    /**
     * Generates the 'action' parameter for event tracking.
     * @param {String} action i.e 'click' or 'enter'
     * @param {Object} suggestion An object which has a key called `type` i.e('query' or 'profile')
     */
    _getAnalyticsEventAction(action, suggestion) {
        var type;

        if (this.getSearchTerm() === "") {
            type = "previous_search_term";
        } else if (suggestion.type) {
            type = `${suggestion.type}_suggestion`;
        }

        return action + "_on_" + type;
    },

    /**
     * @returns {HTMLElement}
     * @private
     */
    _getAutoCompleteWrapper() {
        return this.el.querySelector(AUTOCOMPLETE_WRAPPER_SELECTOR);
    },

    // These two methods below probably won't be necessary once new auto-complete component exists
    _showAutoComplete() {
        this._getAutoComplete().show();
        this._getAutoCompleteWrapper().style.display = "block";
        this.emit("appHeaderAutoCompleteOpened");
    },

    _hideAutoComplete() {
        this._getAutoComplete().hide();
        this._getAutoCompleteWrapper().style.display = null;
        this.emit("appHeaderAutoCompleteClosed");
    },
});
